import { useEffect, useState, useMemo, useCallback } from 'react';
import { ProjectStatusBadges } from './ProjectStatusBadges';
import Modal from './Modal';
import { useAuth } from '../auth/useAuth';
import { isAdminUser } from '../auth/admin';
import type { UserClaims } from '@okta/okta-auth-js';
import { useAppDispatch, useAppSelector } from '../hooks/redux';
import {
  loadAvailableProjects,
  loadProject,
  deleteProject,
  selectAvailableProjects,
  selectProjectsStatus,
  selectProjectsError,
} from '../features/thread/threadSlice';
import { ProjectType, type CatalogMode } from '../api/sophia';
import { confirmLeaveUnsavedMp } from '../hooks/useConfirmLeaveUnsavedMp';
import { getCatalogModeLabel } from '../types/catalogMode';
import { abbreviate, distinctValues } from '../utils/projectDisplay';

interface StorageModalProps {
  isOpen: boolean;
  onClose: () => void;
  onProjectLoaded?: (projectType?: ProjectType) => void;
}

type SortField = 'name' | 'lastEditTime' | 'projectType';
type SortDirection = 'asc' | 'desc';
type ProjectTypeFilter = 'all' | ProjectType;
type CatalogModeFilter = 'all' | CatalogMode | 'legacy';

export function StorageModal({ isOpen, onClose, onProjectLoaded }: StorageModalProps) {
  const dispatch = useAppDispatch();
  const { authState, oktaAuth } = useAuth();
  const projects = useAppSelector(selectAvailableProjects);
  const status = useAppSelector(selectProjectsStatus);
  const error = useAppSelector(selectProjectsError);

  const [sortField, setSortField] = useState<SortField>('lastEditTime');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [filterType, setFilterType] = useState<ProjectTypeFilter>('all');
  // SPJ-1372: il filtro "Stagione" ora filtra per stagione numerica (es. "272"), non piu' per catalogMode.
  const [filterSeason, setFilterSeason] = useState<string>('all');

  // Filtri distintivi + ricerca (SPJ-1372)
  const [searchText, setSearchText] = useState('');
  const [filterConsegna, setFilterConsegna] = useState('');
  const [filterMacrotema, setFilterMacrotema] = useState('');
  const [filterGender, setFilterGender] = useState('');
  const [filterCreator, setFilterCreator] = useState('');

  const [user, setUser] = useState<UserClaims | null>(null);
  const isAdmin = useMemo(() => isAdminUser(user), [user]);

  // Load projects when modal opens
  useEffect(() => {
    if (isOpen && status === 'idle') {
      dispatch(loadAvailableProjects());
    }
  }, [isOpen, status, dispatch]);

  // Info utente per determinare l'admin (badge incognito)
  useEffect(() => {
    let isMounted = true;

    if (!authState?.isAuthenticated) {
      setUser(null);
      return () => {
        isMounted = false;
      };
    }

    if (oktaAuth && 'getUser' in oktaAuth && typeof oktaAuth.getUser === 'function') {
      oktaAuth
        .getUser()
        .then((info: UserClaims) => {
          if (isMounted) setUser(info);
        })
        .catch((err: Error) => {
          console.error('Failed to get user info:', err);
          if (isMounted) setUser(null);
        });
    }

    return () => {
      isMounted = false;
    };
  }, [authState, oktaAuth]);

  const handleSort = useCallback((field: SortField) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  }, [sortField]);

  // Valori distinti per i filtri select (SPJ-1372)
  // SPJ-1372: opzioni Stagione = stagioni numeriche distinte, ordinate numericamente ("262" < "272").
  const seasonOptions = useMemo(
    () => distinctValues(projects.map((p) => p.season)).sort((a, b) => Number(a) - Number(b)),
    [projects],
  );
  // SPJ-1372: le consegne (e i macrotemi) dipendono dalla stagione → le opzioni dei filtri
  // contestuali si calcolano solo sui progetti della Stagione selezionata.
  const contextProjects = useMemo(() => {
    return projects.filter((project) => {
      if (filterSeason !== 'all') return project.season === filterSeason;
      return true;
    });
  }, [projects, filterSeason]);
  const consegnaOptions = useMemo(() => distinctValues(contextProjects.map((p) => p.consegna)), [contextProjects]);
  const macrotemaOptions = useMemo(() => distinctValues(contextProjects.map((p) => p.macrotema)), [contextProjects]);
  const genderOptions = useMemo(() => distinctValues(projects.map((p) => p.gender)), [projects]);
  const creatorOptions = useMemo(() => distinctValues(projects.map((p) => p.creatorName)), [projects]);

  const sortedProjects = useMemo(() => {
    const query = searchText.trim().toLowerCase();
    // First filter by type
    let filtered = projects;
    if (filterType !== 'all') {
      filtered = filtered.filter((project) => project.projectType === filterType);
    }
    if (filterSeason !== 'all') {
      filtered = filtered.filter((project) => project.season === filterSeason);
    }
    if (filterConsegna) {
      filtered = filtered.filter((project) => project.consegna === filterConsegna);
    }
    if (filterMacrotema) {
      filtered = filtered.filter((project) => project.macrotema === filterMacrotema);
    }
    if (filterGender) {
      filtered = filtered.filter((project) => project.gender === filterGender);
    }
    if (filterCreator) {
      filtered = filtered.filter((project) => project.creatorName === filterCreator);
    }
    if (query) {
      filtered = filtered.filter((project) => {
        const haystack = [project.name, project.optimizationId, project.creatorName]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return haystack.includes(query);
      });
    }

    // Then sort
    const sorted = [...filtered].sort((a, b) => {
      let aValue: string | number;
      let bValue: string | number;

      if (sortField === 'name') {
        aValue = a.name.toLowerCase();
        bValue = b.name.toLowerCase();
      } else if (sortField === 'projectType') {
        aValue = a.projectType || '';
        bValue = b.projectType || '';
      } else {
        aValue = a.lastEditTime;
        bValue = b.lastEditTime;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [
    projects,
    sortField,
    sortDirection,
    filterSeason,
    filterType,
    filterConsegna,
    filterMacrotema,
    filterGender,
    filterCreator,
    searchText,
  ]);

  const selectedProject = useMemo(
    () => projects.find((project) => project.threadId === selectedProjectId) ?? null,
    [projects, selectedProjectId],
  );

  const formatDate = useCallback((timestamp: number): string => {
    if (!timestamp) return 'Unknown';

    const date = new Date(timestamp);
    return date.toLocaleDateString(undefined, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }, []);

  const handleLoadProject = useCallback(async () => {
    if (selectedProjectId && selectedProject?.canLoad) {
      // SPJ-1372: caricare un altro progetto abbandona l'editor MP; se ci sono modifiche non
      // salvate chiedi conferma prima di cambiare progetto attivo.
      if (!confirmLeaveUnsavedMp()) return;
      try {
        await dispatch(loadProject(selectedProjectId)).unwrap();
        onProjectLoaded?.(selectedProject.projectType);
        onClose();
      } catch (error) {
        console.error('Failed to load project:', error);
      }
    }
  }, [selectedProject, selectedProjectId, dispatch, onClose, onProjectLoaded]);

  const handleRefresh = useCallback(() => {
    dispatch(loadAvailableProjects());
  }, [dispatch]);

  const handleDeleteProject = useCallback(async (threadId: string, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent row selection when clicking delete

    if (window.confirm('Sei sicuro di voler eliminare questo progetto? Questa azione non può essere annullata.')) {
      try {
        await dispatch(deleteProject(threadId)).unwrap();
        // If the deleted project was selected, clear the selection
        if (selectedProjectId === threadId) {
          setSelectedProjectId(null);
        }
      } catch (error) {
        console.error('Failed to delete project:', error);
        alert('Errore durante l\'eliminazione del progetto. Riprova.');
      }
    }
  }, [dispatch, selectedProjectId]);

  const getSortIcon = useCallback((field: SortField) => {
    if (sortField !== field) return '↕';
    return sortDirection === 'asc' ? '↑' : '↓';
  }, [sortField, sortDirection]);

  const getProjectTypeLabel = useCallback((type?: ProjectType): string => {
    if (!type) return 'Non specificato';
    return type === 'optimization' ? 'Ottimizzazione' : 'Consultazione';
  }, []);

  const getProjectTypeIcon = useCallback((type?: ProjectType): string => {
    if (!type) return '❓';
    return type === 'optimization' ? '⚙️' : '💬';
  }, []);

  const getCatalogModeText = useCallback((mode?: CatalogMode): string => {
    if (!mode) return '—';
    return `${mode} · ${getCatalogModeLabel(mode)}`;
  }, []);

  // SPJ-1372: cambiare Tipo via da 'optimization' resetta i filtri contestuali,
  // così un filtro nascosto (Linea/Consegna/Macrotema) non filtra silenziosamente.
  const handleFilterTypeChange = useCallback((value: ProjectTypeFilter) => {
    setFilterType(value);
    if (value !== 'optimization') {
      setFilterGender('');
      setFilterConsegna('');
      setFilterMacrotema('');
    }
  }, []);

  // SPJ-1372: al cambio di Stagione azzera i filtri Consegna/Macrotema (le consegne valide
  // cambiano con la stagione, quindi un valore selezionato potrebbe non essere piu' valido).
  const handleFilterSeasonChange = useCallback((value: string) => {
    setFilterSeason(value);
    setFilterConsegna('');
    setFilterMacrotema('');
  }, []);

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="flex max-w-[98vw] flex-col bg-white" style={{ width: '1100px', height: '80vh' }}>
        <div className="flex w-full items-center justify-between gap-2 bg-white p-2 border-b border-gray-200">
          <div className="flex flex-row items-center justify-start">
            <h2 className="pl-3 text-2xl font-bold">Carica Progetto</h2>
          </div>
          <div className="flex flex-row gap-2 justify-end items-center">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">Stagione:</label>
              <select
                value={filterSeason}
                onChange={(e) => handleFilterSeasonChange(e.target.value)}
                className="border border-gray-300 px-3 py-2 text-sm"
              >
                <option value="all">Tutte</option>
                {seasonOptions.map((value) => (
                  <option key={value} value={value}>{value}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">Tipo:</label>
              <select
                value={filterType}
                onChange={(e) => handleFilterTypeChange(e.target.value as ProjectTypeFilter)}
                className="border border-gray-300 px-3 py-2 text-sm"
              >
                <option value="all">Tutti</option>
                <option value="consultation">Consultazione</option>
                <option value="optimization">Ottimizzazione</option>
              </select>
            </div>
            <button
              type="button"
              onClick={handleRefresh}
              disabled={status === 'loading'}
              className="border border-gray-300 px-4 py-2 text-sm font-semibold disabled:opacity-50"
            >
              Aggiorna
            </button>
            <button type="button" onClick={onClose} className="flex items-center justify-center w-10 h-10 text-2xl hover:bg-gray-100 transition-colors">×</button>
          </div>
        </div>

        {/* Barra filtri distintivi + ricerca (SPJ-1372) */}
        {status === 'succeeded' && projects.length > 0 && (
          <div className="flex w-full flex-wrap items-center gap-2 border-b border-gray-200 bg-gray-50 px-4 py-2">
            <div className="relative min-w-[200px] flex-1">
              <i className="ti-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" aria-hidden="true" />
              <input
                type="text"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="Cerca per titolo, id o creatore"
                className="w-full border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm text-gray-700 focus:border-[#9F6460] focus:outline-none"
              />
            </div>
            {/* Filtri contestuali: solo per Ottimizzazione (Linea, poi Consegna, poi Macrotema) */}
            {filterType === 'optimization' && (
              <>
                <select
                  value={filterGender}
                  onChange={(e) => setFilterGender(e.target.value)}
                  className="border border-gray-300 bg-white px-2 py-2 text-sm text-gray-700 focus:border-[#9F6460] focus:outline-none"
                  aria-label="Filtra per linea"
                >
                  <option value="">Linea: tutte</option>
                  {genderOptions.map((value) => (
                    <option key={value} value={value}>{value}</option>
                  ))}
                </select>
                <select
                  value={filterConsegna}
                  onChange={(e) => setFilterConsegna(e.target.value)}
                  className="border border-gray-300 bg-white px-2 py-2 text-sm text-gray-700 focus:border-[#9F6460] focus:outline-none"
                  aria-label="Filtra per consegna"
                >
                  <option value="">Consegna: tutte</option>
                  {consegnaOptions.map((value) => (
                    <option key={value} value={value}>{value}</option>
                  ))}
                </select>
                <select
                  value={filterMacrotema}
                  onChange={(e) => setFilterMacrotema(e.target.value)}
                  className="border border-gray-300 bg-white px-2 py-2 text-sm text-gray-700 focus:border-[#9F6460] focus:outline-none"
                  aria-label="Filtra per macrotema"
                >
                  <option value="">Macrotema: tutti</option>
                  {macrotemaOptions.map((value) => (
                    <option key={value} value={value}>{value}</option>
                  ))}
                </select>
              </>
            )}
            {creatorOptions.length > 0 && (
              <select
                value={filterCreator}
                onChange={(e) => setFilterCreator(e.target.value)}
                className="border border-gray-300 bg-white px-2 py-2 text-sm text-gray-700 focus:border-[#9F6460] focus:outline-none"
                aria-label="Filtra per creatore"
              >
                <option value="">Creatore: tutti</option>
                {creatorOptions.map((value) => (
                  <option key={value} value={value}>{value}</option>
                ))}
              </select>
            )}
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-4">
          {status === 'loading' && <p className="p-4 text-sm text-gray-500">Caricamento progetti...</p>}

          {status === 'failed' && error && <p className="p-4 text-sm text-red-500">{error}</p>}

          {status === 'succeeded' && (
            <div className="flex w-full flex-col items-center gap-4">
              {projects.length === 0 ? (
                <p className="text-gray-500">Nessun progetto disponibile</p>
              ) : sortedProjects.length === 0 ? (
                <p className="text-gray-500">Nessun progetto corrisponde ai filtri selezionati</p>
              ) : (
                <div className="flex w-full flex-col gap-2">
                  <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2 text-sm font-semibold text-gray-700">
                    <div className="col-span-1"></div>
                    <div
                      className="col-span-4 flex cursor-pointer items-center gap-2 transition-colors hover:text-gray-900"
                      onClick={() => handleSort('name')}
                    >
                      <span>Nome Progetto</span>
                      <span className="text-gray-400">{getSortIcon('name')}</span>
                    </div>
                    <div
                      className="col-span-2 flex cursor-pointer items-center gap-2 transition-colors hover:text-gray-900"
                      onClick={() => handleSort('projectType')}
                    >
                      <span>Tipo</span>
                      <span className="text-gray-400">{getSortIcon('projectType')}</span>
                    </div>
                    <div className="col-span-2">Stagione</div>
                    <div
                      className="col-span-2 flex cursor-pointer items-center gap-2 transition-colors hover:text-gray-900"
                      onClick={() => handleSort('lastEditTime')}
                    >
                      <span>Ultima Modifica</span>
                      <span className="text-gray-400">{getSortIcon('lastEditTime')}</span>
                    </div>
                    <div className="col-span-1"></div>
                  </div>
                  {sortedProjects.map((project) => {
                    const consegnaSigla = abbreviate(project.consegna);
                    const macrotemaSigla = abbreviate(project.macrotema);
                    return (
                    <div
                      key={project.threadId}
                      className={`grid grid-cols-12 items-center gap-2 border-b border-gray-100 p-3 transition-colors ${
                        !project.canLoad
                          ? 'cursor-not-allowed bg-gray-50 opacity-70'
                          : selectedProjectId === project.threadId
                            ? 'cursor-pointer bg-gray-100'
                            : 'cursor-pointer hover:bg-gray-50'
                      }`}
                      onClick={() => {
                        if (project.canLoad) {
                          setSelectedProjectId(project.threadId);
                        }
                      }}
                    >
                      <div className="col-span-1">
                        <input
                          type="radio"
                          name="selectedProject"
                          checked={selectedProjectId === project.threadId}
                          disabled={!project.canLoad}
                          onChange={() => setSelectedProjectId(project.threadId)}
                          className="w-4 h-4 accent-[#688096]"
                        />
                      </div>
                      <div className="col-span-4">
                        <div className="flex flex-wrap items-center gap-2">
                          {project.optimizationId && (
                            <span className="border border-gray-300 bg-gray-50 px-2 py-0.5 font-mono text-[11px] font-semibold text-gray-700">
                              #{project.optimizationId}
                            </span>
                          )}
                          <span className="font-medium text-gray-900 truncate">{project.name}</span>
                          {isAdmin && project.incognito && (
                            <span className="border border-purple-300 bg-purple-50 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-purple-700">
                              incognito/test
                            </span>
                          )}
                        </div>
                        {(project.gender || consegnaSigla.label || macrotemaSigla.label || project.creatorName || project.lastEditorName) && (
                          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-600">
                            {project.gender && (
                              <span><span className="text-gray-400">Linea:</span> {project.gender}</span>
                            )}
                            {consegnaSigla.label && (
                              <span title={consegnaSigla.title}><span className="text-gray-400">Consegna:</span> {consegnaSigla.label}</span>
                            )}
                            {macrotemaSigla.label && (
                              <span title={macrotemaSigla.title}><span className="text-gray-400">Macrotema:</span> {macrotemaSigla.label}</span>
                            )}
                            {(project.creatorName || project.lastEditorName) && (
                              // SPJ-1372: sotto il creatore, chi ha fatto l'ultima modifica
                              <span className="inline-flex flex-col leading-tight">
                                {project.creatorName && (
                                  <span><span className="text-gray-400">Creato da:</span> {project.creatorName}</span>
                                )}
                                {project.lastEditorName && (
                                  <span
                                    className="text-teal-600"
                                    title={project.lastEditorTime ? `Ultima modifica: ${formatDate(project.lastEditorTime)}` : 'Ultima modifica'}
                                  >
                                    <span className="text-gray-400">Modificato da:</span> {project.lastEditorName}
                                  </span>
                                )}
                              </span>
                            )}
                          </div>
                        )}
                        {!project.canLoad && project.loadDisabledReason && (
                          <div className="mt-1 text-xs text-amber-700">
                            {project.lockedByName && <i className="ti-lock mr-1" aria-hidden="true" />}
                            {project.loadDisabledReason}
                          </div>
                        )}
                        <ProjectStatusBadges project={project} />
                      </div>
                      <div className="col-span-2">
                        <div className="flex items-center gap-2">
                          <span className="text-base">{getProjectTypeIcon(project.projectType)}</span>
                          <span className={`text-sm font-medium ${
                            project.projectType === 'optimization' ? 'text-blue-700' :
                            project.projectType === 'consultation' ? 'text-green-700' :
                            'text-gray-500'
                          }`}>
                            {getProjectTypeLabel(project.projectType)}
                          </span>
                        </div>
                      </div>
                      <div className="col-span-2">
                        {project.season && (
                          <div className="text-sm font-semibold text-gray-900">Stagione {project.season}</div>
                        )}
                        <div className={project.season ? 'text-xs text-gray-500' : 'text-sm text-gray-700'}>
                          {getCatalogModeText(project.catalogMode)}
                        </div>
                      </div>
                      <div className="col-span-2">
                        <div className="text-sm text-gray-600">{formatDate(project.lastEditTime)}</div>
                      </div>
                      <div className="col-span-1 flex justify-center">
                        <button
                          type="button"
                          onClick={(e) => handleDeleteProject(project.threadId, e)}
                          className="px-3 py-2 text-xs font-semibold bg-white text-black border-2 border-black h-10"
                          title="Elimina progetto"
                        >
                          <i className="ti-trash text-base font-bold" aria-hidden="true" />
                        </button>
                      </div>
                    </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-between items-center w-full p-4 bg-white border-t border-gray-200">
          <div className="text-sm text-gray-600">
            {sortedProjects.length} {sortedProjects.length === 1 ? 'progetto trovato' : 'progetti trovati'}
            {(filterType !== 'all' || filterSeason !== 'all') && (
              <span className="ml-2 text-gray-500">
                (
                {[
                  filterType !== 'all' ? (filterType === 'optimization' ? 'Ottimizzazione' : 'Consultazione') : null,
                  filterSeason !== 'all' ? `Stagione ${filterSeason}` : null,
                ].filter(Boolean).join(' · ')}
                )
              </span>
            )}
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-semibold bg-white text-black border-2 border-black"
            >
              Annulla
            </button>
            <button
              type="button"
              onClick={handleLoadProject}
              disabled={!selectedProjectId || !selectedProject?.canLoad}
              className="px-4 py-2 text-sm font-semibold bg-black text-white border-2 border-black disabled:bg-gray-300 disabled:border-gray-300 disabled:cursor-not-allowed"
            >
              Carica Progetto
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

export default StorageModal;
